import { DiscordSDK } from "@discord/embedded-app-sdk";

export interface DiscordAuthResult {
  accessToken: string;
  user: { id: string; username: string; global_name: string | null };
  channelId: string | null;
  guildId: string | null;
  instanceId: string;
}

export interface GetDiscordAuthOptions {
  clientId: string;
  /** Exchange the `code` from `authorize` for an `access_token`. In our flow the
   *  auth-broker's `/mint` ultimately produces the Firebase custom token; the
   *  SDK's own authenticate returns an access token that we also forward to
   *  the broker. */
  tokenExchange: (input: { code: string }) => Promise<{ access_token: string }>;
  /** Optional progress hook — fires just before the SDK's authorize prompt. */
  onAuthorize?: () => void;
}

let cached: DiscordAuthResult | undefined;

export async function getDiscordAuth(options: GetDiscordAuthOptions): Promise<DiscordAuthResult> {
  if (cached) return cached;

  const sdk = new DiscordSDK(options.clientId);
  await sdk.ready();

  options.onAuthorize?.();
  const { code } = await sdk.commands.authorize({
    client_id: options.clientId,
    response_type: "code",
    state: "",
    prompt: "none",
    scope: ["identify", "guilds", "rpc.activities.write"],
  });

  const { access_token } = await options.tokenExchange({ code });

  const authenticated = await sdk.commands.authenticate({ access_token });

  cached = {
    accessToken: authenticated.access_token,
    user: {
      id: authenticated.user.id,
      username: authenticated.user.username,
      global_name: authenticated.user.global_name ?? null,
    },
    channelId: sdk.channelId ?? null,
    guildId: sdk.guildId ?? null,
    instanceId: sdk.instanceId,
  };
  return cached;
}

/** Testing helper: clear the cached auth. */
export function __resetDiscordAuth(): void {
  cached = undefined;
}
